import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { Plus, Pencil, Trash2, MoreHorizontal, Eye } from 'lucide-react'
import { formatDate } from '@/lib/dateUtils'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { PageHeader, DataTable } from '@/components/shared'
import { emailTemplateService } from '@/services/emailTemplateService'

const Templates = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [deletingTemplate, setDeletingTemplate] = useState(null)

  const { data, isLoading } = useQuery('email-templates', emailTemplateService.getAllTemplates, {
    onError: () => toast.error('Failed to fetch templates'),
  })
  const templates = data?.data?.templates || []

  const handleDelete = async () => {
    if (!deletingTemplate) return
    try {
      await emailTemplateService.deleteTemplate(deletingTemplate.id)
      toast.success('Template deleted')
      queryClient.invalidateQueries('email-templates')
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete template')
    } finally {
      setDeletingTemplate(null)
    }
  }

  const columns = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <span className="font-medium text-foreground">{row.original.name}</span>
      ),
    },
    {
      accessorKey: 'subject_line',
      header: 'Subject Line',
      cell: ({ row }) => row.original.subject_line || '—',
    },
    {
      accessorKey: 'updated_at',
      header: 'Last Updated',
      cell: ({ row }) => formatDate(row.original.updated_at),
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuItem onClick={() => navigate(`/marketing-email/templates/${row.original.id}/preview`)}>
              <Eye className="mr-2 h-4 w-4" />
              Preview
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate(`/marketing-email/templates/${row.original.id}/edit`)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => setDeletingTemplate(row.original)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Templates"
        description="Reusable email templates for your campaigns"
        actions={
          <Button size="sm" onClick={() => navigate('/marketing-email/templates/new')}>
            <Plus className="mr-2 h-4 w-4" />
            New Template
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={templates}
        loading={isLoading}
        searchPlaceholder="Search templates..."
        emptyMessage="No templates yet. Create one to use in your campaigns."
        onRowClick={(template) => navigate(`/marketing-email/templates/${template.id}/edit`)}
      />

      <AlertDialog open={!!deletingTemplate} onOpenChange={() => setDeletingTemplate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingTemplate?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default Templates
